import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as InviteActions from '../../stores/actions/invitations';
import classNames from 'classnames';
import type { Invitation } from '../../stores/types/invitations';
import InvitePresenterRow from './invite-presenter-row';
import InviteRequest from './join-request';
import { ApproveJoinRequest, RejectJoinRequest } from '../../connection/join-request';
import { showAlert } from '../general-components/alert-service';
import logger from '../../utils/logger-rum';

function usePrevious(item: any) {
	const prev = useRef(item);
	useEffect(() => {
		prev.current = item;
	}, [item]);
	return prev.current;
}

export default function InvitePresenterAlert(props: any) {
	const joinRequests = useSelector((state: any) => state.InvitationsReducer.joinRequests);
	const token = useSelector((state: any) => state.SessionReducer.token);
	const askToJoin = useSelector((state: any) => state.SettingsReducer.askToJoin);
	const dispatch = useDispatch();
	const [open, setOpen] = useState(false);
	const lastRequests = useRef([...joinRequests]);

	useEffect(() => {
		if (!askToJoin) {
			return setOpen(false);
		}
		if (joinRequests.length > lastRequests.current.length) {
			setOpen(true);
		}

		if (joinRequests.length === 0 && lastRequests.current.length > 0) {
			setOpen(false);
		}

		lastRequests.current = [...joinRequests];
	}, [joinRequests, askToJoin]);

	function close() {
		setOpen(false);
	}


	async function approveRequest(invite: Invitation) {
		try {
			if (invite.join_request) {
				const approved = await ApproveJoinRequest(token, invite.join_request);
				if (approved) dispatch(InviteActions.removeJoinRequest(invite));
				else {
					showAlert({
						message: 'Approval Error',
						description: 'We ran into an issue approving this request. Please try again.',
						duration: 4000,
						type: 'error',
					});
				}
			}
		} catch (e) {
			logger.error(e);
			showAlert({
				message: 'Approval Error',
				description: 'We ran into an issue approving this request. Please try again.',
				duration: 4000,
				type: 'error',
			});
		}
	}

	async function blockRequest(invite: Invitation) {
		try {
			if (invite.join_request) {
				const rejected = await RejectJoinRequest(token, invite.join_request);
				if (rejected) dispatch(InviteActions.removeJoinRequest(invite));
				else {
					showAlert({
						message: 'Rejection Error',
						description: 'We ran into an issue rejecting this request. Please try again.',
						duration: 4000,
						type: 'error',
					});
				}
			}
		} catch (e) {
			logger.error(e);
			showAlert({
				message: 'Rejection Error',
				description: 'We ran into an issue rejecting this request. Please try again.',
				duration: 4000,
				type: 'error',
			});
		}
	}

	return (
		<div className={classNames('invitations-alert', { isOpen: open })}>
			<div className="invitation-alert-header">
				<span className="bl-font-l2">Pending requests {joinRequests.length}</span>
				<button className="dismiss" onClick={close}><span className="bl-font-l2">Dismiss</span></button>
			</div>
			<div className="invites">
				{joinRequests.map((request: Invitation, index: number) => (
					<InviteRequest
						invite={request}
						approve={approveRequest}
						block={blockRequest}
						small={true}
						key={index}
					/>
				))}
			</div>
		</div>
	);
}